import { useMemo } from 'react';

import { Chip, Divider, Text } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { formatNumber } from '~/utils/common';

import type { DeliveryResultReportSummary } from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyReportSummary.module.scss';

const SUMMARY_ITEMS = [
  { label: '전체 운송장', className: 'total', textColor: 'kurly', valueKey: 'totalCount' },
  {
    label: '배송 완료',
    className: 'completed',
    textColor: 'success',
    valueKey: 'DELIVERY_COMPLETED',
    sub: [
      { label: '대응배송', valueKey: 'ALTERNATIVE_DELIVERY' },
      { label: '지연배송', valueKey: 'DELAYED' },
      // TODO: 당일배송 배포시 주석 해제
      // { label: '전환배송', valueKey: 'CONVERTED_DELIVERY' },
    ],
  },
  { label: '배송 중', className: 'on-the-way', textColor: 'info', valueKey: 'ON_THE_WAY' },
  { label: '배송 실패', className: 'failed', textColor: 'warning', valueKey: 'DELIVERY_FAILED' },
  { label: '배송 취소', className: 'canceled', textColor: 'tertiary', valueKey: 'DELIVERY_CANCELED' },
] as const;

type TypeSummaryItem = (typeof SUMMARY_ITEMS)[number];

export type TypeDeliveryAgencyReportSummaryValueKey = TypeSummaryItem['valueKey'];

export type TypeDeliveryAgencyReportSummarySubValueKey = Extract<
  TypeSummaryItem,
  { sub: readonly unknown[] }
>['sub'][number]['valueKey'];

const buildCountByValueKey = (summary: DeliveryResultReportSummary | null): Record<string, number> => {
  const map: Record<string, number> = { totalCount: summary?.totalCount ?? 0 };
  summary?.shippingLabelStatusCounts.forEach(({ code, count }) => (map[code] = count));
  summary?.deliveryResultCounts.forEach(({ code, count }) => (map[code] = count));
  return map;
};

interface DeliveryAgencyReportSummaryProps {
  summary: DeliveryResultReportSummary | null;
  selectedValueKeys: TypeDeliveryAgencyReportSummaryValueKey[];
  selectedSubValueKeys: TypeDeliveryAgencyReportSummarySubValueKey[];
  onSelect: (valueKey: TypeDeliveryAgencyReportSummaryValueKey) => void;
  onToggleSub: (subValueKey: TypeDeliveryAgencyReportSummarySubValueKey) => void;
}

/**
 * [배송 결과 리포트] 서머리 컴포넌트
 *
 */
const DeliveryAgencyReportSummary = ({
  summary,
  selectedValueKeys,
  selectedSubValueKeys,
  onSelect,
  onToggleSub,
}: DeliveryAgencyReportSummaryProps) => {
  const data = useMemo(() => buildCountByValueKey(summary), [summary]);

  return (
    <div className={styles.container}>
      {SUMMARY_ITEMS.map((item) => (
        <button
          key={item.valueKey}
          type="button"
          className={clsx(styles.box, styles[item.className], {
            [styles.active]: selectedValueKeys.includes(item.valueKey),
          })}
          onClick={() => onSelect(item.valueKey)}
        >
          <div className={styles.main}>
            <Text size="large" weight="semi-bold" color={item.textColor}>
              {item.label}
            </Text>
            <Text size="xxlarge" weight="semi-bold" color={item.textColor}>
              {formatNumber(data[item.valueKey] ?? 0)}건
            </Text>
          </div>
          {'sub' in item && (
            <>
              <Divider color="gray100" orientation="vertical" className={styles['sub-divider']} />
              <div className={styles['chip-wrap']}>
                {item.sub.map((subItem) => (
                  <Chip
                    key={subItem.valueKey}
                    size="medium"
                    variant="stroke"
                    color="success"
                    active={selectedSubValueKeys.includes(subItem.valueKey)}
                    className={styles.chip}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleSub(subItem.valueKey);
                    }}
                  >
                    {subItem.label} {formatNumber(data[subItem.valueKey] ?? 0)}건
                  </Chip>
                ))}
              </div>
            </>
          )}
        </button>
      ))}
    </div>
  );
};

export { DeliveryAgencyReportSummary };
